import { Listbox as HeadlessListbox, ListboxButton, ListboxOption, ListboxOptions, ListboxLabel } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { cn } from '../../lib/utils';

interface ListboxOptionType {
  value: string;
  label: string;
  variant?: 'default' | 'danger';
}

interface ListboxProps {
  label: string;
  options: ListboxOptionType[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export default function Listbox({ label, options, value, onChange, className }: ListboxProps) {
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <HeadlessListbox value={value} onChange={onChange}>
      <div className={cn("relative", className)}>
        <ListboxLabel className="block text-[10px] font-bold uppercase tracking-[0.1em] text-white/20 mb-2 px-1">
          {label}
        </ListboxLabel>
        <ListboxButton
          className={cn(
            'relative block w-full rounded-xl bg-white/[0.02] py-2.5 pl-4 pr-10 text-left text-sm text-white backdrop-blur-sm transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]',
            'focus:outline-none data-[focus]:bg-white/[0.08] data-[focus]:shadow-lg data-[focus]:shadow-blue-500/10 data-[focus]:backdrop-blur-lg',
            'data-[focus]:ring-1 data-[focus]:ring-inset data-[focus]:ring-white/20 data-[hover]:bg-white/[0.06]'
          )}
        >
          <span className="block truncate font-medium">
            {selectedOption?.label || '请选择'}
          </span>
          <ChevronDownIcon
            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 size-5 fill-white/30"
            aria-hidden="true"
          />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          transition
          className={cn(
            'z-[100] w-[var(--button-width)] overflow-auto rounded-xl focus:outline-none sm:text-sm custom-scrollbar [--anchor-gap:8px]',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            '-mt-[1px] rounded-t-none rounded-b-xl bg-white/[0.02] backdrop-blur-xl shadow-2xl shadow-black/50 p-1'
          )}
        >
          {options.map((option, index) => (
            <div key={option.value}>
              {/* 在危险操作前添加分割线 */}
              {option.variant === 'danger' && index > 0 && options[index - 1]?.variant !== 'danger' && (
                <div className="my-1 h-[0.5px] bg-white/5 w-full" />
              )}
                <ListboxOption
                  value={option.value}
                  className={({ active, selected }) => cn(
                    'relative cursor-pointer select-none rounded-lg py-2.5 pl-10 pr-4 transition-colors duration-150',
                    (active && selected)
                      ? 'bg-blue-600 text-white font-medium'
                      : selected
                        ? 'bg-blue-500 text-white font-medium'
                        : active
                          ? option.variant === 'danger'
                            ? 'bg-red-500/10 text-red-400'
                            : 'bg-white/[0.12] text-white'
                          : 'text-white/60'
                  )}
                >
                {({ selected }) => (
                  <>
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-500">
                      <CheckIcon className={cn('h-4 w-4', selected ? 'visible' : 'invisible')} />
                    </span>
                    <span className="block truncate text-inherit">
                      {option.label}
                    </span>
                  </>
                )}
              </ListboxOption>
            </div>
          ))}
        </ListboxOptions>
      </div>
    </HeadlessListbox>
  );
}

export type { ListboxOptionType };